import type { ComponentProps, ReactElement } from 'react';
import type React from 'react';

import { SocialSharing } from '@change/ds-core-ui';
import {
	CheckIcon,
	EmailIcon,
	FacebookIcon,
	FacebookMessengerIcon,
	InstagramIcon,
	LineIcon,
	LinkIcon,
	NextDoorIcon,
	RedditIcon,
	ShareIcon,
	SmsIcon,
	TikTokIcon,
	TwitterIcon,
	VkIcon,
	WhatsAppIcon,
} from '@change/ds-icons';
import { cn } from '@change/ds-utils';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';

import type { ShareAction as ShareActionType, ShareChannel } from '#src/app/shared/hooks/share';

import { useShareButton } from './hooks/useShareButton';

type Channel = ShareChannel;
type SocialSharingVariant = NonNullable<ComponentProps<typeof SocialSharing>['variant']>;

// Channel -> brand-coloured SocialSharing variant. Channels without a DS brand variant
// (copy/email/sms/line/vk/native share/google) fall back to the neutral 'default'.
const CHANNEL_VARIANT: Partial<Record<Channel, SocialSharingVariant>> = {
	facebook: 'facebook',
	facebookMessenger: 'facebookMessenger',
	instagram: 'instagram',
	instagramStories: 'instagram',
	twitter: 'twitter',
	whatsapp: 'whatsapp',
	reddit: 'reddit',
	nextdoor: 'nextdoor',
	tiktok: 'tiktok',
};

const CHANNEL_ICON: Record<Channel, ReactElement> = {
	copy: <LinkIcon />,
	email: <EmailIcon />,
	facebook: <FacebookIcon />,
	facebookMessenger: <FacebookMessengerIcon />,
	instagram: <InstagramIcon />,
	instagramStories: <InstagramIcon />,
	line: <LineIcon />,
	mobileNativeShare: <ShareIcon />,
	nextdoor: <NextDoorIcon />,
	reddit: <RedditIcon />,
	sms: <SmsIcon />,
	tiktok: <TikTokIcon />,
	twitter: <TwitterIcon />,
	vk: <VkIcon />,
	whatsapp: <WhatsAppIcon />,
};

type ShareButtonProps = {
	buttonConfig: {
		ShareAction: ShareActionType<Channel>['ShareAction'];
		channel: Channel;
		label: TranslationKey;
		successLabel?: TranslationKey;
		labelSize?: 'default' | 'small';
	};
	isSelected?: boolean;
	onBeforeAction?: () => void | Promise<void>;
};
export const ShareButton: React.FC<ShareButtonProps> = ({
	buttonConfig: { ShareAction, channel, label, successLabel, labelSize },
	isSelected = false,
	onBeforeAction,
}) => {
	const { typedTranslate } = useI18n();
	const { data, actions } = useShareButton({ label, successLabel });

	return (
		<ShareAction onBeforeAction={onBeforeAction} onAfterAction={actions.afterShare}>
			<SocialSharing
				variant={CHANNEL_VARIANT[channel] ?? 'default'}
				icon={data.clickedState ? <CheckIcon data-testid="share-button-success-icon" /> : CHANNEL_ICON[channel]}
				label={typedTranslate(data.buttonLabel)}
				size={labelSize === 'small' ? 'medium' : 'large'}
				className={cn('w-full', isSelected && 'border-2 border-black')}
			/>
		</ShareAction>
	);
};
